import { UseFormReturn } from 'react-hook-form';
import {
  Form,
  FormField,
  FormItem,
  FormMessage,
} from '@web/components/ui/form';
import { InputAnimatedLabel } from '@web/components/ui/input-animated-label';
import { ProductData } from '@shared/types';
import { Switch } from '@web/components/ui/switch';

type ProductDetailsFormProps = {
  form: UseFormReturn<ProductData>;
  onSubmit: (data: ProductData) => Promise<void>;
  isEditMode: boolean;
  setIsSubmitting: (isSubmitting: boolean) => void;
};

export function ProductDetailsForm({
  form,
  onSubmit,
  isEditMode,
  setIsSubmitting,
}: Readonly<ProductDetailsFormProps>) {
  return (
    <Form {...form}>
      <form
        onSubmit={(e) => {
          setIsSubmitting(true);

          void form.handleSubmit(onSubmit)(e);
        }}
      >
        <div className="grid gap-6 md:grid-cols-2">
          {/* Product Fields */}
          <div className="space-y-4 md:col-span-2">
            <FormField
              control={form.control}
              name="displayName"
              render={({ field }) => (
                <FormItem>
                  <InputAnimatedLabel
                    label="Display Name"
                    {...field}
                    readOnly={!isEditMode}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="productAlias"
              render={({ field }) => (
                <FormItem>
                  <InputAnimatedLabel
                    label="Product Alias"
                    {...field}
                    readOnly={!isEditMode}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Description Fields */}
          <FormField
            control={form.control}
            name="shortDescription"
            render={({ field }) => (
              <FormItem>
                <InputAnimatedLabel
                  label="Short Description"
                  {...field}
                  readOnly={!isEditMode}
                />
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="description"
            render={({ field }) => (
              <FormItem>
                <InputAnimatedLabel
                  label="Description"
                  {...field}
                  readOnly={!isEditMode}
                />
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Financial Fields */}
          <FormField
            control={form.control}
            name="salesPrice"
            render={({ field: { onChange, ...field } }) => (
              <FormItem>
                <InputAnimatedLabel
                  label="Sales Price"
                  type="number"
                  {...field}
                  onChange={(e) => {
                    const value = Number(e.target.value);
                    const max = 100000;
                    if (value >= 0 && value <= max) {
                      onChange(value);
                    }
                  }}
                  readOnly={!isEditMode}
                  inputMode="decimal"
                  min={0}
                  step={1}
                  max={100000}
                />
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="commissionAmount"
            render={({ field: { onChange, ...field } }) => (
              <FormItem>
                <InputAnimatedLabel
                  label="Commission Amount"
                  type="number"
                  {...field}
                  onChange={(e) => {
                    const value = Number(e.target.value);
                    const max = form.watch('commissionIsPercent')
                      ? 100
                      : 999999.99;
                    if (value >= 0 && value <= max) {
                      onChange(value);
                    }
                  }}
                  readOnly={!isEditMode}
                  inputMode="decimal"
                  min={0}
                  step={1}
                  max={form.watch('commissionIsPercent') ? '100' : '999999.99'}
                />
                <FormMessage />
              </FormItem>
            )}
          />

          {/* Additional Fields */}
          <FormField
            control={form.control}
            name="category"
            render={({ field }) => (
              <FormItem>
                <InputAnimatedLabel
                  label="Category"
                  {...field}
                  readOnly={!isEditMode}
                />
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="commissionIsPercent"
            render={({ field }) => (
              <FormItem className="flex items-center space-x-3">
                <Switch
                  id="commission-is-percent-switch"
                  checked={field.value}
                  disabled={!isEditMode}
                  onCheckedChange={(status: boolean) => {
                    form.setValue('commissionIsPercent', status);
                  }}
                />
                <span className="text-sm font-medium">
                  Commission Is Percent
                </span>
              </FormItem>
            )}
          />
        </div>

        <button type="submit" className="hidden" />
      </form>
    </Form>
  );
}
